/* eslint-disable react/jsx-key */
import * as React from "react";
import { Table, Segment, Pagination, Icon, Input, Form, Grid, TableCellProps, TableHeaderCellProps, TableRow, TableCell, Label, FormField, GridColumn, GridRow, FormGroup, TableHeader, TableBody, TableHeaderCell } from "@/components/bp/bp-ui";
import { useTable, Column, TableOptions, usePagination, useExpanded, useSortBy, useAsyncDebounce, useGlobalFilter, SortingRule, useFilters, Filters, defaultColumn, Cell } from "react-table";
import { Dropdown } from "@/components/bp/bp-ui";
import { insertComponentsIntoText } from "./Utils";
import { useRef } from "react";
import { CleanTable } from "./CleanTable";
// import reactStringReplace from 'react-string-replace'


// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface BPReactTableOwnProps<T extends object = {}> {
    disablePagination?: boolean;
    stickyHeader?: boolean;
    renderRowSubComponent?: (row: T) => React.ReactElement
    onGlobalFilter?: (filterValue: any) => void
    onFilters?: (filters: Filters<T>) => void
    onSort?: (rules: Array<SortingRule<T>>) => void
    onChangePage?: (page: number) => void
    onChangePageSize?: (pageSize: number) => void
    subHeaderComponent?: React.ReactElement;
    totalRows?: number;
    compact?: boolean;
    celled?: boolean;
    striped?: boolean;
    singleLine?: boolean;
    fixed?: boolean;
    definition?: boolean;
    columns: Array<BPColumn<T>>;
    collapsing?: boolean;
    loading?: boolean;
    forPrint?: boolean;
    deletedSelector?: (row: T) => boolean;
    rowCssNameProvider?: (row: T) => string;
}

export type BPColumn<T extends Object = {}> = Column<T> & {
    columns?: Array<BPColumn<T>>;
    tableCellProps?: TableCellProps;
    tableHeaderProps?: TableHeaderCellProps;
    defaultFilterValue?: any;
    storeKey?: string;
    disableSortBy?: boolean;
    cellTitle?: ((cell: Cell<T>) => string) | boolean | null;
    hidden?: boolean;
    collapsing?: boolean;
    // omit?: boolean
}

export type BPReactTableProps<T extends object = {}> = BPReactTableOwnProps<T> & TableOptions<T>;

export const BPReactTable: <T extends object = {}>(props: BPReactTableProps<T>) => React.ReactElement<BPReactTableProps<T>> = (props) => {

    const isSortable = !props.disableSortBy
    const isPageable = !props.disablePagination
    const isExpandable = !!props.renderRowSubComponent
    const isStickyHeader = props.stickyHeader
    const hasGlobalFilter = !props.disableGlobalFilter
    const hasFilter = !props.disableFilters

    const plugins = [];

    if (hasGlobalFilter)
        plugins.push(useGlobalFilter)
    if (hasFilter)
        plugins.push(useFilters)
    if (isSortable)
        plugins.push(useSortBy);
    if (isExpandable)
        plugins.push(useExpanded);
    if (isPageable)
        plugins.push(usePagination);

    const defColumn = React.useMemo(
        () => ({
            width: null
        }),
        []
    )

    const {
        gotoPage,
        // globalFilter,
        setGlobalFilter,
        preGlobalFilteredRows,
        getTableProps,
        headerGroups,
        getTableBodyProps,
        prepareRow,
        rows,
        page,
        visibleColumns,
        columns,
        pageCount,
        setPageSize,
        state: { sortBy, pageSize, pageIndex, globalFilter: stateGlobalFilter, filters }
    } = useTable({
        data: React.useMemo(() => props.data, []),
        columns: React.useMemo(() => props.columns, []),
        defaultColumn: defColumn,
        // initialState: { hiddenColumns: props.columns.filter(x => x.hidden).map(x => x.id) }, 
        ...props
    }, ...plugins);

    const isFirstRunPage = useRef(true);
    const isFirstRunSort = useRef(true);
    const isFirstRunGlobalFilter = useRef(true);
    const isFirstRunFilters = useRef(true);

    if (isPageable || isSortable) {
        React.useEffect(() => {
            if (isFirstRunSort.current) {
                isFirstRunSort.current = false;
                return;
            }

            // console.log('sort');

            if (props.onSort && sortBy && sortBy.length > 0)
                props.onSort(sortBy)
        }, [sortBy]);

        React.useEffect(() => {
            if (isFirstRunPage.current) {
                isFirstRunPage.current = false;
                return;
            }

            if (isPageable) {
                if (props.onChangePageSize)
                    props.onChangePageSize(pageSize)
            }
        }, [pageSize]);
    }
    if (hasGlobalFilter) {
        React.useEffect(() => {
            if (isFirstRunGlobalFilter.current) {
                isFirstRunGlobalFilter.current = false;
                return;
            }

            // console.log('global filter');

            if (props.onGlobalFilter)
                props.onGlobalFilter(stateGlobalFilter || '')
        }, [stateGlobalFilter]);
    }

    if (hasFilter) {
        React.useEffect(() => {
            if (isFirstRunFilters.current) {
                isFirstRunFilters.current = false;
                return;
            }

            // console.log('filter');

            if (props.onFilters)
                props.onFilters(filters as any)
        }, [filters]);
    }

    return (
        <Form as="div" loading={props.loading}>
            {(isPageable || hasFilter || props.subHeaderComponent || hasGlobalFilter) &&
                <Segment basic className="no-padding no-margin">
                    <Grid verticalAlign='middle'>
                        <GridRow columns={'equal'}>
                            <GridColumn>
                                {isPageable &&
                                    <FormField inline>
                                        {insertComponentsIntoText(window.translation.dataTable.lengthMenu, {
                                            '_MENU_': <Dropdown
                                                key='pageSizeDD'
                                                compact
                                                selection
                                                options={[10, 25, 50, 100].map(size => { return { key: size, value: size, text: size } })}
                                                value={pageSize}
                                                onChange={(e, dprops) => {
                                                    setPageSize(Number(dprops.value))
                                                }}
                                            />
                                        })}
                                    </FormField>
                                }
                            </GridColumn>
                            <GridColumn textAlign="right" style={{ flex: '0 0 auto', width: 'auto' }}>
                                {/* <div style={{ position: 'absolute', paddingRight: '1rem', right: 0 }}> */}
                                <FormGroup inline style={{ margin: 0 }}>
                                    {props.subHeaderComponent &&
                                        <>
                                            {props.subHeaderComponent}
                                        </>
                                    }
                                    {hasFilter &&
                                        <>
                                            {columns.filter(c => c.Filter).map(c => {
                                                return (
                                                    <FormField key={`filter-col${c.id}`}>
                                                        {c.render('Filter')}
                                                    </FormField>
                                                )
                                            })}
                                        </>
                                    }
                                    {hasGlobalFilter &&
                                        <GlobalFilter
                                            preGlobalFilteredRows={preGlobalFilteredRows}
                                            globalFilter={stateGlobalFilter}
                                            setGlobalFilter={setGlobalFilter} />
                                    }
                                </FormGroup>
                                {/* </div> */}
                            </GridColumn>
                        </GridRow>
                    </Grid>
                </Segment>
            }
            <Table as={props.forPrint ? CleanTable : Table} {...getTableProps()}
                attached
                celled={props.celled}
                striped={props.striped}
                compact={props.compact}
                singleLine={props.singleLine}
                fixed={props.fixed}
                definition={props.definition}
                collapsing={props.collapsing}
                sortable={isSortable}
                className={`${props.forPrint ? 'for-print' : ''} ${isStickyHeader ? 'sticky-header' : ''} ${isExpandable ? 'expandable' : ''}`}>
                <TableHeader>
                    {headerGroups.map(hg => {
                        const { key, ...hgprops } = hg.getHeaderGroupProps();
                        return (
                            <TableRow {...hgprops} key={key}>
                                {isExpandable &&
                                    <TableHeaderCell style={{ width: 45 }} />}
                                {hg.headers.map(c => {
                                    const { key, ...hprops } = c.getHeaderProps([
                                        ...isSortable && c.canSort ? [c.getSortByToggleProps()] : [],
                                        ...c.width != null ? [{ style: { width: c.width } }] : [],
                                        ...(c as BPColumn).tableHeaderProps != null ? [(c as BPColumn).tableHeaderProps] : [{ title: c.Header }],
                                        ...(c as BPColumn).collapsing != null ? [{ collapsing: (c as BPColumn).collapsing }] : [],
                                    ]);
                                    return (
                                        <TableHeaderCell key={key}
                                            {...hprops}
                                            sorted={!c.canSort ? null : c.isSorted ? c.isSortedDesc ? 'descending' : 'ascending' : null}
                                        >
                                            {c.render("Header")}
                                        </TableHeaderCell>
                                    );
                                })}</TableRow>
                        );
                    })}
                </TableHeader>
                <TableBody {...getTableBodyProps()}>
                    {(props.disablePagination ? rows : page).map((row, i) => {
                        prepareRow(row);
                        const { key, ...rprops } = row.getRowProps(props.rowCssNameProvider != null ? { className: props.rowCssNameProvider(row.original) } : {});
                        return (
                            <React.Fragment key={`${key}-fragment`}>
                                <TableRow {...rprops}>
                                    {isExpandable &&
                                        <TableCell textAlign='center'>
                                            <Icon {...row.getToggleRowExpandedProps()} name={row.isExpanded ? 'caret down' : 'caret right'}></Icon>
                                        </TableCell>
                                    }

                                    {row.cells.map((cell, cellIdx) => {
                                        const { key, ...cprops } = cell.getCellProps([
                                            ...(cell.column as BPColumn).cellTitle != null ? [{ title: (cell.column as BPColumn).cellTitle == true ? cell.value : (cell.column as BPColumn).cellTitle == false ? '' : (cell.column as any).cellTitle(cell) }] : [],
                                            ...(cell.column as BPColumn).tableCellProps != null ? [(cell.column as BPColumn).tableCellProps] : [],
                                            ...(cell.column as BPColumn).collapsing != null ? [{ collapsing: (cell.column as BPColumn).collapsing }] : []
                                        ]);
                                        return <TableCell key={key} negative={props.deletedSelector != null && props.deletedSelector(row.original)} {...cprops}>
                                            {cell.render("Cell")}
                                        </TableCell>
                                    })}
                                </TableRow>
                                {
                                    isExpandable && row.isExpanded ? (
                                        <TableRow key={`${row.getRowProps().key}-expanded`}>
                                            <TableCell colSpan={visibleColumns.length + (isExpandable ? 1 : 0)}>
                                                {props.renderRowSubComponent(row.original)}
                                            </TableCell>
                                        </TableRow>
                                    ) : null
                                }

                            </React.Fragment>
                        )
                    })}

                    {(props.disablePagination ? rows : page).length == 0 &&
                        <TableRow>
                            <TableCell textAlign={'center'} colSpan={visibleColumns.length}>{window.translation.dataTable.emptyTable}</TableCell>
                        </TableRow>
                    }
                </TableBody>
            </Table>

            {isPageable && page.length > 0 &&
                <Segment basic className="no-padding">
                    <Grid columns='equal'>
                        <GridRow>
                            <GridColumn verticalAlign={'middle'}>
                                <div>
                                    {insertComponentsIntoText(window.translation.dataTable.info, {
                                        '_START_': (pageIndex) * pageSize + 1,
                                        '_END_': Math.min((pageIndex) * pageSize + pageSize, props.totalRows > 0 ? props.totalRows : 0),
                                        '_TOTAL_': props.totalRows > 0 ? props.totalRows : 0,
                                    })}
                                </div>
                            </GridColumn>
                            <GridColumn style={{ textAlign: "right" }}>
                                <Pagination
                                    lastItem={null}
                                    firstItem={null}
                                    nextItem={{ content: window.translation.dataTable.paginate.next }}
                                    prevItem={{ content: window.translation.dataTable.paginate.previous }}
                                    totalPages={pageCount}
                                    activePage={pageIndex + 1}
                                    onPageChange={(e, pageProps) => {
                                        gotoPage(Number(pageProps.activePage) - 1);
                                        if (props.onChangePage)
                                            props.onChangePage(Number(pageProps.activePage))
                                    }}
                                />

                            </GridColumn>
                        </GridRow>
                    </Grid>
                </Segment>
            }
        </Form>
    )
};

// Define a default UI for filtering
function GlobalFilter({
    preGlobalFilteredRows,
    globalFilter,
    setGlobalFilter,
}) {
    const count = preGlobalFilteredRows.length
    const [value, setValue] = React.useState(globalFilter)
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value)
    }, 200)

    return (
        <FormField inline key={`filter-colSearch`}>
            {/* <label>{window.translation.dataTable.search}</label> */}
            <Input
                placeholder={window.translation.dataTable.search}
                icon={'search'}
                value={value || ""}
                onChange={e => {
                    setValue(e.target.value);
                    onChange(e.target.value);
                }}
            />
        </FormField>
    )
}

// // This is a custom filter UI for selecting
// // a unique option from a list
// export function SelectColumnFilter({
//     column: { filterValue, setFilter, preFilteredRows, id },
// }) {
//     // Calculate the options for filtering
//     // using the preFilteredRows
//     const options = React.useMemo(() => {
//         const options = new Set()
//         preFilteredRows.forEach(row => {
//             options.add(row.values[id])
//         })
//         return [...options.values()]
//     }, [id, preFilteredRows])

//     // Render a multi-select box
//     return (
//         <select
//             value={filterValue}
//             onChange={e => {
//                 setFilter(e.target.value || undefined)
//             }}
//         >
//             <option value="">All</option>
//             {options.map((option, i) => (
//                 <option key={i} value={option}>
//                     {option}
//                 </option>
//             ))}
//         </select>
//     )
// }